// @flow
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { DateService } from 'services/date'
import { getSpyValues as spyValues } from 'features/form-spy/helpers'
import type { FormSpyValues } from 'features/form-spy/types'
import { UserInterface } from 'services/user/entities/user.interface'
import type { Grade } from 'core/types'
import { DEFAULT_USER, EXTERNSHIP_PROLONGATION_USER_TAG, EXTERNSHIP_USER_TAG } from '../constants'
import type {
  UserSocialNet,
  UserRole,
  UserType,
  AllUserFields,
  UserAddress,
  UserSchool,
  UserSubscriptions,
  UserInfoAttributes,
  UserDiscipline,
  Privacy,
  CoachPaymentScheme,
} from '../types'
import { getUserRole, getUserInitials } from '../utils'

export class User implements UserInterface {
  // app_options
  address: UserAddress | void

  avatarUrl: string

  birthday: string | null

  bonusAmount: number

  cartTemplateId: number | null

  coach: boolean | void

  coachAgreementAccepted: boolean | void

  coachPaymentScheme: CoachPaymentScheme | void

  createdAt: Dayjs

  disciplineIds: Array<number>

  email: null | string

  emailConfirmed: boolean

  externshipCoachAgreementAccepted: boolean | void

  externshipUser: boolean

  fakeEmail: boolean

  fakeUser: boolean

  firstName: string

  fullName: string

  grade: Grade

  gradeChecked: boolean

  isGraduate: boolean

  hasAccessToMultiDaysStreaks: boolean | void

  hasAccessToAttestationSheets: boolean | void

  hasActivePurchases: boolean

  hasBookmarks: boolean

  hasChats: boolean

  hasSchoolClasses: boolean | void

  hasReferrals: boolean

  id: number

  isCoach: boolean

  isCustomer: boolean

  isElementaryPupil: boolean

  isExternshipCoach: boolean

  isExternshipProlongation: boolean

  isMiniGroupTeacher: boolean

  isParent: boolean

  lastName: string

  methodist: boolean

  middleName: string | null

  onboardingFinished: boolean

  parentPhone: string | null

  parentPhoneConfirmed: boolean

  phone: null | string

  phoneConfirmed: boolean

  profileEnriched: boolean

  shortName: string

  school: UserSchool | void

  skype: null | string

  socialNets: UserSocialNet[]

  tags: string[]

  type: UserType

  unconfirmedEmail: null | string

  userInfoAttributes: UserInfoAttributes | void

  // me
  recentRegistration: boolean

  schoolEntryYear: number | null | void

  // profile
  agentProfessionIds: Array<number>

  birthday: string | null

  cartTemplateId: number | null | void

  disciplines: UserDiscipline[]

  hasActivePurchases: boolean

  lastCertificationDate: number | null | false | void

  parentPhone: string | null

  privacy: Privacy | void

  school: UserSchool | void

  schoolEntryYear: number | null | void

  startTeachingYear: number | null | void

  subscribed: boolean | void

  subscriptions: UserSubscriptions | void

  appearanceId: number | void | null

  hasUnseenRoomFeatures: boolean | void

  // generated
  _type: '' | UserType

  isExternship: boolean

  isPupil: boolean

  isAgent: boolean

  schoolId: number | null | void

  webSocketNotificationsJwt: string | null

  constructor(props: AllUserFields = DEFAULT_USER) {
    if (typeof props !== 'object') return

    const plainKeys: string[] = Object.keys(props).filter((key) => key !== 'type')
    const mutationKeys: string[] = ['type']

    plainKeys.forEach((key) => {
      switch (key) {
        case 'id':
          if (typeof props.id === 'number') {
            this.id = props.id
          } else {
            this.id = 0
          }
          break

        case 'recentRegistration':
          if (props.recentRegistration !== undefined) {
            this.recentRegistration = Boolean(props.recentRegistration)
          } else {
            this.recentRegistration = DEFAULT_USER.recentRegistration
          }

          break

        case 'schoolEntryYear':
          if (typeof props.schoolEntryYear === 'number') {
            this.schoolEntryYear = props.schoolEntryYear
          } else {
            this.schoolEntryYear = DEFAULT_USER.schoolEntryYear
          }

          break

        case 'agentProfessionIds':
          if (props.agentProfessionIds !== undefined) {
            this.agentProfessionIds = props.agentProfessionIds
          } else {
            this.agentProfessionIds = DEFAULT_USER.agentProfessionIds
          }

          break

        case 'birthday':
          if (props.birthday !== undefined) {
            this.birthday = props.birthday
          } else {
            this.birthday = DEFAULT_USER.birthday
          }

          break

        case 'cartTemplateId':
          if (props.cartTemplateId !== undefined) {
            this.cartTemplateId = props.cartTemplateId
          } else {
            this.cartTemplateId = DEFAULT_USER.cartTemplateId
          }

          break

        case 'createdAt':
          this.createdAt =
            dayjs.isDayjs(props.createdAt) && typeof props.createdAt !== 'string'
              ? DateService.tz(props.createdAt.toISOString())
              : typeof props.createdAt === 'string' && props.createdAt !== ''
              ? DateService.tz(props.createdAt)
              : DateService.tz('1970-01-01')

          break

        case 'disciplines':
          if (props.disciplines !== undefined) {
            this.disciplines = props.disciplines
          } else {
            this.disciplines = DEFAULT_USER.disciplines
          }

          break

        case 'isElementaryPupil':
          if (typeof props.isElementaryPupil === 'boolean') {
            this.isElementaryPupil = props.isElementaryPupil
          } else {
            this.isElementaryPupil = Boolean(DEFAULT_USER.isElementaryPupil)
          }

          break

        case 'isExternshipCoach':
          if (typeof props.isElementaryPupil === 'boolean') {
            this.isExternshipCoach = props.isExternshipCoach
          } else {
            this.isExternshipCoach = Boolean(DEFAULT_USER.isExternshipCoach)
          }

          break

        case 'hasActivePurchases':
          if (typeof props.isElementaryPupil === 'boolean') {
            this.hasActivePurchases = props.hasActivePurchases
          } else {
            this.hasActivePurchases = DEFAULT_USER.hasActivePurchases
          }

          break

        case 'parentPhone':
          if (typeof props.parentPhone === 'string') {
            this.parentPhone = props.parentPhone
          } else {
            this.parentPhone = DEFAULT_USER.parentPhone
          }

          break

        case 'tags':
          if (Array.isArray(props.tags)) {
            this.tags = props.tags
          } else {
            this.tags = DEFAULT_USER.tags
          }

          break

        default:
          Object.assign(this, { [key]: props[key] })

          break
      }
    })

    mutationKeys.forEach((key) => {
      const mutatedField = props[`_${key}`]

      Object.assign(this, { [`_${key}`]: mutatedField !== undefined ? mutatedField : props[key] })
    })

    if (props.cartTemplateId === undefined) {
      this.cartTemplateId = null
    }

    if (props.agentProfessionIds === undefined) {
      this.agentProfessionIds = []
    }

    this.isPupil = this._type === 'pupil'

    this.isAgent = this._type === 'agent' || props.isCoach

    this.isExternship = this.tags.includes(EXTERNSHIP_USER_TAG)

    this.isExternshipProlongation = this.tags.includes(EXTERNSHIP_PROLONGATION_USER_TAG)

    this.schoolId = props.school?.id ?? null
  }

  // $FlowFixMe[unsafe-getters-setters]
  get type(): UserType {
    const spy = this.getSpyValues()

    return this._type
      ? this._type
      : typeof spy.type === 'string' && ['pupil', 'agent'].includes(spy.type)
      ? spy.type
      : 'pupil'
  }

  // $FlowFixMe[unsafe-getters-setters]
  set type(value: UserType) {
    this._type = value
  }

  getRole(): UserRole {
    return getUserRole(this)
  }

  getInitials(): string {
    return getUserInitials(this)
  }

  // eslint-disable-next-line class-methods-use-this
  getSpyValues(): FormSpyValues {
    return spyValues()
  }

  isLoggedIn(): boolean {
    return this.id > 0
  }
}
